import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { lngDetect } from "../../../../helpers/lngDetect";
import Button from "../../../form/Button";
import OrganizationItem from "../../../items/OrganizationItem";
import { useDebounce } from "../../../../hooks/useDebounce";
import EventItemLoader from "../../../loader/EventItemLoader";
import { followList } from "../../../../services/followService";
import ArtistItem from "./ArtistItem";
import NoData from "../../../elements/Nodata";
import noDataImage from "../../../../assets/images/dance.svg";

const ArtistList = () => {
  const { lang } = useSelector((state) => state?.langProvider);
  const [removedItems, setRemovedItems] = useState([]);
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const queryClient = useQueryClient();
  const { data } = useSelector((state) => state?.profile);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 1,
    page: 1,
    sortQuery: "-createdAt",
  });

  const {
    data: followerData,
    isLoading: followerLoading,
    isFetching: followerFetching,
    refetch,
  } = useQuery({
    queryKey: ["userFollowerData", filters, data?._id, search],
    queryFn: async () => {
      const res = await followList({
        ...filters,
        ...(search && { keyword: search }),
        user: data?._id,
        referenceType: "user",
      });

      if (res?.status !== 200) {
        toast.warning(res?.message);
      }

      if (res?.docs?.status === 200) {
        const new_data_arr = res?.docs?.data;

        if (search) {
          return {
            ...res?.docs,
            data: [...new_data_arr],
          };
        } else {
          if (filters?.page > 1) {
            return {
              ...res?.docs,
              data: [...(followerData?.data || []), ...new_data_arr]?.filter(
                (item, index, self) =>
                  index === self.findIndex((t) => t?._id === item?._id)
              ),
            };
          } else {
            return {
              ...res?.docs,
              data: new_data_arr,
            };
          }
        }
      } else {
        return {
          ...(res?.docs || res),
          data: [],
        };
      }
    },
    enabled: !!data?._id,
    placeholderData: keepPreviousData,
  });

  async function addToFavoriteResponse(res) {
    if (res?.status === 200) {
      queryClient.setQueryData(
        ["follower", filters, data?._id, search],
        (state) => {
          let state_data = JSON.parse(JSON.stringify(state || {}));
          const new_arr = state_data?.data?.filter(
            (item) => item?._id !== res?.data?._id
          );
          setFilters((pre) => ({ ...pre, page: 1 }));
          return {
            ...state_data,
            data: new_arr,
          };
        }
      );
      queryClient.invalidateQueries({
        queryKey: ["follower", filters, data?._id, search],
      });
    }
  }

  const loadMore = () => {
    if (followerData?.metadata?.hasNextPage) {
      setFilters((pre) => ({
        ...pre,
        page: followerData?.metadata?.nextPage,
      }));
    }
  };

  function getResponseFollow(res) {
    if (res?.status === 200 || res?.status === 201) {
      followList({
        ...filters,
        ...(search && { keyword: search }),
        user: data?._id,
        referenceType: "user",
      }).then((resp) => {
        queryClient.setQueryData(
          ["userFollowerData", filters, data?._id, search],
          (state) => {
            const prevData = state?.data || [];
            // Remove the target item directly
            const filteredData = prevData.filter(
              (item) => item?.artistData?.user !== res?.data?.to
            );

            // Build a Set of existing IDs
            const existingIds = new Set(filteredData.map((item) => item?._id));

            // Only take new items that aren't already in state
            const result = (resp?.docs?.data || []).filter(
              (item) => !existingIds.has(item?._id)
            );

            return {
              ...state,
              data: [...filteredData, ...result],
              metadata: resp?.docs?.metadata,
            };
          }
        );
      });
    }
  }

  return (
    <>
      {followerLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 2xl:gap-8">
          {Array.from({ length: 8 }, (_, i) => (
            <EventItemLoader key={i} />
          ))}
        </div>
      ) : followerData?.data?.length > 0 ? (
        <div className="grid md:grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 2xl:gap-8">
          {followerData?.data.map((artist) => (
            <ArtistItem
              key={artist?.id || artist?._id}
              artist={artist?.artistData}
              getResponse={getResponseFollow}
            />
          ))}
        </div>
      ) : (
        <NoData
          image={noDataImage}
          message="Currently you are not follow any artist"
        />
      )}
      {
        followerData?.data?.length > 0 && (
          // followerData?.data?.length < followerData?.metadata?.totalDocs && (
          <div className="flex space-y-4 items-center justify-between mt-6">
            <div className="text-deep-purple text-xl 2xl:text-2xl font-semibold text-center">
              Showing {followerData?.data?.length} of{" "}
              {followerData?.metadata?.totalDocs}
            </div>
            {followerData?.metadata?.hasNextPage && (
              <div className="flex justify-center">
                <Button
                  buttonLabel="Load More"
                  buttonLabelClasses="text-lg uppercase text-white"
                  buttonClasses="h-14 bg-secondary-gradient"
                  buttonFunction={loadMore}
                  {...(followerFetching && {
                    loading: true,
                    buttonIconPosition: "left",
                    isDisable: true,
                  })}
                />
              </div>
            )}
          </div>
        )
        // )
      }
    </>
  );
};

export default ArtistList;
